import { useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { Creators as DataCreators } from "model/data"
import { Creators as NotificationCreators } from "modules/notifications/model"
import { useAppDispatch, useAppStore } from "hooks/redux"
import type { ReduxState } from "model/state"
import { DASHBOARD_INSTANCES_RESOURCE } from "resources/dashboard-instances"
import { useRemoveInstance } from "./use-remove-instance"

export interface DeleteDashboard {
  // Deletes the instance, not the backing layout. Never throws — toasts on success/failure.
  remove: (instanceId: string) => Promise<void>
}

/**
 * Deletes a dashboard instance: server DELETE + store teardown, resets the instances cache so the
 * resolver won't rehydrate the deleted row, then navigates to bare /:account/dashboard for the
 * resolver to pick a fallback. The backing layout is kept; the workspace state is lost.
 */
export function useDeleteDashboard(): DeleteDashboard {
  const store = useAppStore()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const removeInstance = useRemoveInstance()

  const remove = useCallback(
    async (instanceId: string) => {
      try {
        // DELETE the instance server-side, then tear down its full local store footprint.
        await removeInstance(instanceId)

        // Reset so the resolver refetches without the deleted id (the cached list would rehydrate it).
        dispatch(DataCreators.resetCollections([DASHBOARD_INSTANCES_RESOURCE]))

        // Read fresh from the store, not a render closure — this can resolve after an account
        // switch, same as useDuplicateDashboard. [APP-3309]
        const account = (store.getState() as unknown as ReduxState).global.selected.account
        // Bare route — the resolver picks a surviving dashboard (we don't target an id).
        if (account) navigate(`/${account}/dashboard`)

        dispatch(NotificationCreators.add("success", null, "Deleted the dashboard."))
      } catch {
        dispatch(NotificationCreators.add("error", null, "Couldn't delete the dashboard. Please try again."))
      }
    },
    [store, dispatch, navigate, removeInstance],
  )

  return { remove }
}
